import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Stack, useTheme, TablePagination } from '@mui/material';

import { ApiService } from '../../../../Services/ApiService';
import { IPositionSummary } from '../../../../Models/IPositionSummary';
import logger from '../../../../Services/LoggingService';

// Import all extracted modules
import { 
  BlindTestListProps, 
  useBlindTestListState 
} from './BlindTestListTypes';
import { styles } from './BlindTestListStyles';
import { 
  BlindTestListHeader,
  BlindTestListFilter,
  BlindTestListLoading,
  BlindTestListEmpty,
  BlindTestTable,
  ColumnMenu,
  ColumnManagerDialog
} from './BlindTestListComponents';
import {
  createHandlePageChange,
  createHandleChangeRowsPerPage,
  createHandleColumnFilterChange,
  createHandleSortMenuClick,
  createColumnMenuHandlers,
  createHandleToggleFilters,
  createNavigationHandlers
} from './BlindTestListEventHandlers';
import {
  createBlindTestColumns,
  createGetDesignSessionData,
  createSetColumnsFromConfig
} from './BlindTestListApi';
import {
  getSortedAndFilteredData,
  createFetchAllPagesData,
  isColumnVisible,
  getVisibleColumns
} from './BlindTestListUtils';

/**
 * BlindTestList component displays the list of blind tests using Material UI
 */
export const BlindTestList = ({ unitConfiguration: propUnitConfiguration, blindTests }: BlindTestListProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { positionSummary: IPositionSummary } };
  const ctx = useMsal();
  const apiService = new ApiService(ctx);

  // Initialize state using custom hook
  const blindTestState = useBlindTestListState(propUnitConfiguration, state);

  // Initialize blindTest data from props if provided
  useEffect(() => {
    logger.debug('BlindTestList initialized with props', { 
      blindTestsCount: blindTests?.length || 0 
    }, {
      component: 'BlindTestList',
      method: 'useEffect - props initialization'
    });
    
    if (blindTests && blindTests.length > 0) {
      blindTestState.setCurrentData(blindTests);
      const columns = createBlindTestColumns(
        blindTests[0], 
        handleNavigateToDetail, 
        blindTestState.preferredFlowrateUnits,
        theme
      );
      blindTestState.setColumns(columns);
      blindTestState.setIsLoading(false);
    }
  }, [blindTests, theme.palette.error.dark, theme.palette.primary.main]);

  // Track changes to propUnitConfiguration and update internal state
  useEffect(() => {
    if (propUnitConfiguration) {
      blindTestState.setUnitInputConfiguration(propUnitConfiguration);
      logger.debug('Unit configuration updated from props', propUnitConfiguration, {
        component: 'BlindTestList',
        method: 'useEffect - unit configuration update'
      });
    }
  }, [propUnitConfiguration]);

  useEffect(()=> {
    logger.debug('BlindTestList monitoring unit configuration changes', propUnitConfiguration, {
      component: 'BlindTestList',
      method: 'useEffect - configuration monitoring'
    });
  }, [propUnitConfiguration])

  // Create event handler functions using factories
  const getDesignSessionData = createGetDesignSessionData(
    apiService, 
    blindTestState, 
    state?.positionSummary, 
    blindTestState.configurations
  );

  const setColumnsFromConfig = createSetColumnsFromConfig(
    apiService,
    blindTestState,
    (item) => handleNavigateToDetail(item),
    theme,
    blindTests
  );

  const fetchAllPagesData = createFetchAllPagesData(
    getDesignSessionData,
    blindTestState.rowsPerPage,
    blindTestState.currentFilter
  );

  // Create all event handlers using factory functions
  const handlePageChange = createHandlePageChange(blindTestState, getDesignSessionData);
  const handleChangeRowsPerPage = createHandleChangeRowsPerPage(blindTestState, getDesignSessionData);
  const handleColumnFilterChange = createHandleColumnFilterChange(blindTestState, fetchAllPagesData, getDesignSessionData);
  const handleSortMenuClick = createHandleSortMenuClick(blindTestState, fetchAllPagesData);
  const handleToggleFilters = createHandleToggleFilters(blindTestState);

  const columnMenuHandlers = createColumnMenuHandlers(blindTestState);
  const navigationHandlers = createNavigationHandlers(navigate);

  // Helper functions
  const isColumnVisibleHelper = (columnId: string) => isColumnVisible(columnId, blindTestState.hiddenColumns);
  const getVisibleColumnsHelper = () => getVisibleColumns(blindTestState.columns, blindTestState.hiddenColumns);
  const getSortedAndFilteredDataHelper = () => getSortedAndFilteredData(
    blindTestState.currentData,
    blindTestState.filterValue,
    blindTestState.filterColumn,
    blindTestState.orderBy,
    blindTestState.order,
    blindTestState.currentPage,
    blindTestState.rowsPerPage
  );
    
  // Navigation functions
  const handleNavigateBack = () => navigationHandlers.handleNavigateBack();
  const handleNavigateToDetail = (item: any) => navigationHandlers.handleNavigateToDetail(
    item, 
    blindTestState.unitInputConfiguration, 
    blindTestState.configurations
  );

  // Get data and build dynamic columns when configurations change
  useEffect(() => {
    if (blindTestState.configurations.length === 0) {
      setColumnsFromConfig();
    }

    if (!blindTests && state?.positionSummary?.blindTestDataSetId) {
      getDesignSessionData();
    }
  }, [blindTestState.configurations, blindTests]);

  return (
    <Stack spacing={3} sx={styles.pageContainer}>
      {/* Header */}
      <BlindTestListHeader
        positionSummary={state?.positionSummary}
        onNavigateBack={handleNavigateBack}
        onToggleFilters={handleToggleFilters}
        showFilters={blindTestState.showFilters}
        isLoading={blindTestState.isLoading}
      />

      {/* Filter */}
      <BlindTestListFilter
        showFilters={blindTestState.showFilters}
        columns={blindTestState.columns}
        filterColumn={blindTestState.filterColumn}
        filterValue={blindTestState.filterValue}
        currentFilter={blindTestState.currentFilter}
        onFilterChange={handleColumnFilterChange}
        onSetFilterColumn={blindTestState.setFilterColumn}
              />

      {/* Main Content */}
      {blindTestState.isLoading ? (
        <BlindTestListLoading />
      ) : (
        <>
          {blindTestState.currentData && blindTestState.currentData.length > 0 && blindTestState.columns.length > 0 && (
            <>
              <BlindTestTable
                columns={blindTestState.columns}
                data={getSortedAndFilteredDataHelper()}
                visibleColumns={getVisibleColumnsHelper()}
                onItemClick={handleNavigateToDetail}
                onOpenColumnMenu={columnMenuHandlers.handleOpenColumnMenu}
              />
              
              {/* Column Menu */}
              <ColumnMenu
                anchorEl={blindTestState.columnMenuAnchor}
                activeColumn={blindTestState.activeColumn}
                onClose={columnMenuHandlers.handleCloseColumnMenu}
                onSortMenuClick={handleSortMenuClick}
                onToggleColumnVisibility={columnMenuHandlers.handleToggleColumnVisibility}
                onOpenColumnManager={columnMenuHandlers.handleOpenColumnManager}
              />
              
              {/* Column Manager Dialog */}
              <ColumnManagerDialog
                open={blindTestState.columnManagerOpen}
                columns={blindTestState.columns}
                onClose={columnMenuHandlers.handleCloseColumnManager}
                onToggleColumnVisibility={columnMenuHandlers.handleToggleColumnVisibility}
                isColumnVisible={isColumnVisibleHelper}
              />

              {/* Pagination - only for design session data */}
              {!blindTests && (
                <TablePagination
                  rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                  component="div"
                  count={blindTestState.filterValue && blindTestState.filterColumn ? getSortedAndFilteredDataHelper().length : (blindTestState.rowsPerPage === -1 ? blindTestState.currentData.length : blindTestState.pageCount * blindTestState.rowsPerPage)}
                  rowsPerPage={blindTestState.rowsPerPage}
                  page={blindTestState.currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page:"
                />
              )}
            </>
          )}
          
          {(!blindTestState.currentData || blindTestState.currentData.length === 0) && !blindTestState.isLoading && (
            <BlindTestListEmpty />
          )}
        </>
      )}
    </Stack>
  );
}; 